import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import PostCreator from '../components/PostCreator';
import PostCard from '../components/PostCard';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Loader2 } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const Feed = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchPosts();
  }, [filter]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?type=${filter}` : '';
      const { data } = await api.get(`/posts${params}`);
      setPosts(data);
    } catch (error) {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handlePostCreated = (newPost) => {
    setPosts([newPost, ...posts]);
  };

  const handlePostDeleted = (postId) => {
    setPosts(posts.filter(p => p.id !== postId));
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Left Sidebar */}
          <div className="hidden lg:block lg:col-span-3 sticky top-24 h-fit">
            <Sidebar />
          </div>

          {/* Main Feed */}
          <div className="col-span-12 lg:col-span-6 space-y-6" data-testid="feed-container">
            <PostCreator onPostCreated={handlePostCreated} />

            {/* Filter Tabs */}
            <Tabs value={filter} onValueChange={setFilter}>
              <TabsList className="w-full grid grid-cols-4 bg-white">
                <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
                <TabsTrigger value="win" data-testid="filter-win">Wins</TabsTrigger>
                <TabsTrigger value="question" data-testid="filter-question">Questions</TabsTrigger>
                <TabsTrigger value="update" data-testid="filter-update">Updates</TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Posts */}
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-[#1E3A8A]" />
              </div>
            ) : posts.length === 0 ? (
              <div className="bg-white rounded-md p-12 text-center">
                <p className="text-slate-500">No posts yet. Be the first to share!</p>
              </div>
            ) : (
              <div className="space-y-6">
                {posts.map((post) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    onUpdate={fetchPosts}
                    onDelete={handlePostDeleted}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right Sidebar - Placeholder for future widgets */}
          <div className="hidden xl:block xl:col-span-3 sticky top-24 h-fit">
            <div className="bg-white rounded-md border border-slate-100 shadow-sm p-6">
              <h3 className="font-oswald font-bold text-lg uppercase text-[#1E3A8A] mb-4">
                Community Pulse
              </h3>
              <div className="space-y-3 text-sm text-slate-600">
                <p className="flex items-center justify-between">
                  <span>Active Members</span>
                  <span className="font-bold text-[#22C55E]">Live</span>
                </p>
                <p className="text-xs text-slate-500">
                  Join the conversation and share your journey!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Feed;
